import { ElementProps } from '@/components/sections/builder/types';
import React from 'react';
import { ElementDataType } from './types';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { WrapperChild } from '@/components/sections/builder/wrapper';
import { cn } from '@/lib/utils';
import clsx from 'clsx';
import Autoplay from 'embla-carousel-autoplay';

export default function ElementContent({ element, preview, selectedId }: ElementProps) {
  const content = element.data as ElementDataType;
  const prevButton = React.useRef<HTMLButtonElement>(null);
  const nextButton = React.useRef<HTMLButtonElement>(null);
  const childs = element.childs || [];
  const childIds = childs.map((v) => v.id);
  const childSelected = !!selectedId && childIds.includes(selectedId);
  const autoplay = content.autoplay ? true : false;

  React.useEffect(() => {
    if (childSelected) {
      const idx = childIds.indexOf(selectedId);
      api?.scrollTo(idx, true);
    }
  }, [selectedId]);

  const delay = content.autoplay ? content.changeSlidesEvery * 1000 : 0;
  const plugin = React.useRef(
    Autoplay({
      delay: delay,
      playOnInit: autoplay,
    })
  );
  const [api, setApi] = React.useState<CarouselApi>();
  const [index, setIndex] = React.useState(0);

  React.useEffect(() => {
    childIds.forEach(async (childId, idx) => {
      const vid = document.getElementById(`slideshow-video-${childId}`) as HTMLVideoElement;
      if (index === idx) {
        await vid?.play();
      } else {
        vid?.pause();
      }
    });
  }, [index]);

  React.useEffect(() => {
    if (content.autoplay) {
      plugin.current.reset();
    } else {
      plugin.current.stop();
    }
  }, [content.autoplay]);

  React.useEffect(() => {
    if (!api) {
      return;
    }

    setIndex(api.selectedScrollSnap());

    api.on('select', () => {
      setIndex(api.selectedScrollSnap());
    });
  }, [api]);

  const onClickPagination = (idx: number) => {
    api?.scrollTo(idx, true);
  };

  return (
    <>
      <section className={cn('theme_layout_page_width mx-auto bg-white duration-100')}>
        <div className={cn(content.layout === 'full' && 'slideshow-full-width')}>
          <div className='w-full' data-animation='true'>
            <Carousel
              setApi={setApi}
              plugins={autoplay ? [plugin.current] : []}
              className='relative w-full'
              opts={{ loop: true }}
            >
              <CarouselContent>
                {childs.map((child) => (
                  <CarouselItem key={child.id}>
                    <WrapperChild
                      child={{
                        ...child,
                        parentData: content,
                      }}
                      parentType={element.type}
                      preview={preview}
                      className='w-full'
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
              {childs.length > 1 && !content.hideNavigation && (
                <>
                  <CarouselPrevious
                    className='absolute left-4 h-14 w-14 cursor-pointer text-white'
                    iconClassName='w-12 h-12'
                    ref={prevButton as React.ForwardedRef<HTMLButtonElement>}
                  />
                  <CarouselNext
                    className='absolute right-4 h-14 w-14 cursor-pointer text-white'
                    iconClassName='w-12 h-12'
                    ref={nextButton as React.ForwardedRef<HTMLButtonElement>}
                  />
                </>
              )}
            </Carousel>
            <div
              className={clsx(
                'mb-4 mt-6 flex items-center justify-center space-x-6',
                content.hidePagination && 'hidden',
                content.paginationPosition === 'front' && '!m-0',
                content.paginationAlignment === 'left' && '!ml-[40px] !justify-start ',
                content.paginationAlignment === 'right' && '!mr-[40px] !justify-end'
              )}
            >
              {content.paginationStyle === 'counter' ? (
                <div
                  className={clsx(
                    content.hidePagination
                      ? 'hidden'
                      : content.paginationPosition === 'front' && 'pagination-wrapper'
                  )}
                >
                  {index + 1}/{childs?.length}
                </div>
              ) : (
                <div
                  className={clsx(
                    'flex items-center space-x-4',
                    content.hidePagination
                      ? 'hidden'
                      : content.paginationPosition === 'front' && 'pagination-wrapper'
                  )}
                >
                  {childs?.map((_, idx) => {
                    if (content.paginationStyle === 'dots') {
                      return (
                        <div
                          key={idx}
                          onClick={() => onClickPagination(idx)}
                          className={cn(
                            'h-3 w-3 cursor-pointer rounded-full border-2',
                            idx === index && 'bg-primary'
                          )}
                        ></div>
                      );
                    }
                    return (
                      <span
                        key={idx}
                        onClick={() => onClickPagination(idx)}
                        className={cn(idx === index && 'text-base underline', 'cursor-pointer')}
                      >
                        {idx + 1}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      <style jsx>
        {`
          .slideshow-full-width {
            position: relative;
            left: 50%;
            right: 50%;
            margin-left: -50vw;
            margin-right: -50vw;
            width: 100vw;
          }
          .pagination-wrapper {
            position: absolute;
            z-index: 2;
            margin-top: -60px;
          }
        `}
      </style>
    </>
  );
}
